import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { pixelTrack } from "@/lib/pixel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wallet,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Loader2,
  Clock,
  Coins,
  IndianRupee,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

// Load Razorpay checkout script
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Credits() {
  const { user, refreshCredits } = useAuth();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [totalTx, setTotalTx] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addAmount, setAddAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [pricePerCredit, setPricePerCredit] = useState(10);
  const [canTopup, setCanTopup] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [balRes, txRes] = await Promise.all([
        axios.get(`${API}/api/credits/balance`, { withCredentials: true }),
        axios.get(`${API}/api/credits/transactions?page=${page}&limit=15`, { withCredentials: true }),
      ]);
      setBalance(balRes.data.balance);
      setTransactions(txRes.data.transactions);
      setTotalTx(txRes.data.total);
    } catch (err) {
      toast.error("Failed to load credits data");
    }
    try {
      const priceRes = await axios.get(`${API}/api/credits/credit-price`, { withCredentials: true });
      setPricePerCredit(priceRes.data.price_per_credit);
      setCanTopup(priceRes.data.can_topup !== false);
    } catch {
      // credit-price endpoint may not be available; use default
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRazorpayPayment = async () => {
    const credits = parseFloat(addAmount);
    if (!credits || credits <= 0) {
      toast.error("Enter a valid credit amount");
      return;
    }

    setPaying(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        setPaying(false);
        return;
      }

      // Create order on backend
      const orderRes = await axios.post(
        `${API}/api/credits/create-order`,
        { credits },
        { withCredentials: true }
      );
      const { order_id, amount, key_id } = orderRes.data;

      const options = {
        key: key_id || RAZORPAY_KEY_ID,
        amount,
        currency: "INR",
        name: "Prysh AI",
        description: `Top-up ${credits} Credits`,
        order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API}/api/credits/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true }
            );
            setBalance(verifyRes.data.balance);
            pixelTrack("Purchase", { value: amount / 100, currency: "INR" });
            setAddAmount("");
            setShowTopUp(false);
            toast.success(`Added ${verifyRes.data.credits_added} credits!`);
            if (refreshCredits) refreshCredits();
            fetchData();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          } finally {
            setPaying(false);
          }
        },
        prefill: {
          email: user?.email || "",
          name: user?.name || "",
        },
        theme: { color: "#2563eb" },
        modal: {
          ondismiss: () => setPaying(false),
        },
      };

      const rzp = new window.Razorpay(options);
      pixelTrack("InitiateCheckout", { value: amount / 100, currency: "INR" });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setPaying(false);
    }
  };

  const quickAmounts = [10, 25, 50, 100];
  const totalPages = Math.ceil(totalTx / 15);
  const selectedCredits = parseFloat(addAmount) || 0;
  const subtotal = selectedCredits * pricePerCredit;
  const gstAmount = Math.round(subtotal * 0.18 * 100) / 100;
  const totalPrice = Math.round((subtotal + gstAmount) * 100) / 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6" data-testid="credits-page">
      {/* Balance Card */}
      <div className="rounded-xl border bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6" data-testid="credits-balance-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-medium text-white/70">Credit Balance</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/10"
            onClick={fetchData}
            data-testid="refresh-balance-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <div className="text-4xl font-bold mb-4" data-testid="credits-balance-value">
          {balance.toFixed(1)} <span className="text-lg font-normal text-white/50">credits</span>
        </div>

        {!canTopup ? (
          <div className="rounded-lg bg-white/5 border border-white/10 p-4 space-y-2" data-testid="upgrade-prompt">
            <p className="text-sm text-white/70">
              Free tier users cannot top-up credits. Upgrade your plan to add more credits.
            </p>
            <Button
              onClick={() => window.location.href = "/pricing"}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="upgrade-plan-btn"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </div>
        ) : !showTopUp ? (
          <Button
            onClick={() => setShowTopUp(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="show-topup-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Credits
          </Button>
        ) : (
          <div className="space-y-3 pt-2 border-t border-white/10">
            <div className="flex gap-2 flex-wrap">
              {quickAmounts.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAddAmount(String(amt))}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    addAmount === String(amt)
                      ? "bg-blue-600 border-blue-500 text-white"
                      : "border-white/20 text-white/70 hover:border-white/40"
                  }`}
                  data-testid={`quick-amount-${amt}`}
                >
                  {amt} credits
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="number"
                value={addAmount}
                onChange={(e) => setAddAmount(e.target.value)}
                placeholder="Custom amount"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="credit-amount-input"
                min="1"
              />
              <Button
                variant="ghost"
                onClick={() => { setShowTopUp(false); setAddAmount(""); }}
                className="text-white/60 hover:text-white shrink-0"
              >
                Cancel
              </Button>
            </div>
            {selectedCredits > 0 && (
              <div className="bg-white/5 rounded-lg px-3 py-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/70">{selectedCredits} credits</span>
                  <span className="text-sm text-white/70 flex items-center gap-0.5">
                    <IndianRupee className="w-3 h-3" />{subtotal.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">GST (18%)</span>
                  <span className="text-xs text-white/50 flex items-center gap-0.5">
                    <IndianRupee className="w-2.5 h-2.5" />{gstAmount.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-1.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-white">Total</span>
                  <span className="text-base font-semibold text-white flex items-center gap-0.5">
                    <IndianRupee className="w-3.5 h-3.5" />{totalPrice.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            )}
            <Button
              onClick={handleRazorpayPayment}
              disabled={paying || !addAmount || selectedCredits <= 0}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="pay-with-razorpay-btn"
            >
              {paying ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <IndianRupee className="w-4 h-4 mr-1" />
              )}
              {paying ? "Processing..." : `Pay ${totalPrice > 0 ? `₹${totalPrice.toLocaleString("en-IN")}` : ""}`}
            </Button>
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Transaction History
          </h2>
          <span className="text-sm text-muted-foreground">{totalTx} total</span>
        </div>

        {transactions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="no-transactions">
            <Coins className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="transactions-list">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                data-testid={`transaction-${tx.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    tx.type === "credit" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                  }`}>
                    {tx.type === "credit" ? (
                      <ArrowDownRight className="w-4 h-4" />
                    ) : (
                      <ArrowUpRight className="w-4 h-4" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{tx.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(tx.created_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${tx.type === "credit" ? "text-green-600" : "text-red-600"}`}>
                    {tx.type === "credit" ? "+" : "-"}{tx.amount}
                  </p>
                  <p className="text-xs text-muted-foreground">Bal: {tx.balance_after}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
